import { brandConfig } from './brands';

/**
 * ハンバーガーメニュー上部の項目順を表す共通定数とユーティリティ。
 * バックエンド (backend/routes/partner_site_settings.py の MENU_ALLOWED_KEYS) と
 * 同期させること。
 */

export const MENU_ITEM_KEYS = [
  'tags',
  'brands',
  'dashboard',
  'subscription',
  'organizations',
  'profile',
] as const;

export type MenuItemKey = (typeof MENU_ITEM_KEYS)[number];

export const MENU_DIVIDER_KEYS = ['divider_1', 'divider_2', 'divider_3'] as const;
export type MenuDividerKey = (typeof MENU_DIVIDER_KEYS)[number];

export type MenuToken = MenuItemKey | MenuDividerKey;

const ALLOWED_TOKENS: ReadonlySet<MenuToken> = new Set<MenuToken>([
  ...MENU_ITEM_KEYS,
  ...MENU_DIVIDER_KEYS,
]);

/** ブランドごとのフォールバック順 */
const BRAND_DEFAULTS: Record<string, MenuToken[]> = {
  hypeapp: ['tags', 'brands', 'divider_1', 'dashboard', 'subscription', 'organizations', 'profile'],
};

const FALLBACK_ORDER: MenuToken[] = [
  'dashboard',
  'subscription',
  'organizations',
  'brands',
  'tags',
  'profile',
];

/**
 * 受け取った配列を許容トークンのみにフィルタし、必須項目（6種類）を担保する。
 * - 不正・重複は除去
 * - 必須項目で配列に含まれていないものは末尾に追加
 */
export function normalizeMenuOrder(input: readonly unknown[] | null | undefined): MenuToken[] {
  const seen = new Set<MenuToken>();
  const result: MenuToken[] = [];
  if (Array.isArray(input)) {
    for (const value of input) {
      if (typeof value !== 'string') continue;
      const token = value as MenuToken;
      if (!ALLOWED_TOKENS.has(token)) continue;
      if (seen.has(token)) continue;
      seen.add(token);
      result.push(token);
    }
  }
  // 必須項目を担保（存在しないものは末尾追加）
  for (const required of MENU_ITEM_KEYS) {
    if (!seen.has(required)) {
      result.push(required);
      seen.add(required);
    }
  }
  return result;
}

/** API値 → ブランドデフォルト → ハードコードフォールバックの順で解決 */
export function resolveMenuOrder(apiValue: readonly unknown[] | null | undefined): MenuToken[] {
  if (apiValue && Array.isArray(apiValue) && apiValue.length > 0) {
    return normalizeMenuOrder(apiValue);
  }
  const brandDefault = BRAND_DEFAULTS[brandConfig.id];
  if (brandDefault) return normalizeMenuOrder(brandDefault);
  return normalizeMenuOrder(FALLBACK_ORDER);
}

export const MENU_ITEM_LABELS: Record<MenuItemKey, string> = {
  tags: brandConfig.labels.tagManagement,
  brands: 'ブランド管理',
  dashboard: '利用状況',
  subscription: 'プラン・契約',
  organizations: '組織情報',
  profile: 'マイページ',
};

export const MENU_ITEM_PATHS: Record<MenuItemKey, string> = {
  tags: '/tags',
  brands: '/brands',
  dashboard: '/dashboard',
  subscription: '/subscription',
  organizations: '/organizations',
  profile: '/profile',
};

export function isDividerKey(token: string): token is MenuDividerKey {
  return (MENU_DIVIDER_KEYS as readonly string[]).includes(token);
}
